// src/components/modals/EditProductInventoryModal.jsx
import React, { useState, useEffect } from 'react'
import { X, Edit, AlertCircle, Package } from 'lucide-react'
import ProductInventoryService from '../../services/productInventoryService'
import ProductInventoryModel from '../../utils/productInventoryModel'

const EditProductInventoryModal = ({ isOpen, onClose, inventory, onInventoryUpdated }) => {
  const [formData, setFormData] = useState({
    currentStock: ''
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Reset form when modal opens/closes or inventory changes
  useEffect(() => {
    if (isOpen && inventory) {
      console.log('🔧 Initializing form with inventory:', inventory)
      
      setFormData({
        currentStock: inventory.currentStock?.toString() || '0'
      })
      console.log('📝 Form initialized with currentStock:', inventory.currentStock)
      setErrors({})
    }
  }, [isOpen, inventory])

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Handle status toggle - REMOVED

  // Validate form using the model
  const validateForm = () => {
    const validation = ProductInventoryModel.validatePartial(formData)
    setErrors(validation.errors)
    return validation.isValid
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      console.log('❌ Form validation failed:', errors)
      return
    }

    try {
      setLoading(true)
      
      // Prepare data for API - only send changed fields
      const updateData = {}
      
      if (Number(formData.currentStock) !== (inventory.currentStock || 0)) {
        updateData.currentStock = Number(formData.currentStock)
      }
      
      console.log('🔍 Form data vs original:', {
        formCurrentStock: formData.currentStock,
        originalCurrentStock: inventory.currentStock,
        willUpdate: Number(formData.currentStock) !== (inventory.currentStock || 0),
        updateData
      })
      
      // Only proceed if there are changes
      if (Object.keys(updateData).length === 0) {
        console.log('ℹ️ No changes detected')
        onClose()
        return
      }
      
      console.log('📤 Updating inventory:', {
        inventoryId: inventory._id,
        updateData
      })
      
      const response = await ProductInventoryService.updateInventory(inventory._id, updateData)
      
      if (response.success) {
        console.log('✅ Inventory updated successfully:', response)
        
        // Reset form
        setFormData({
          currentStock: ''
        })
        setErrors({})
        
        // Notify parent component with updated data
        const updatedInventory = response.data?.productInventory || { ...inventory, ...updateData }
        console.log('📤 Sending updated inventory to parent:', updatedInventory)
        onInventoryUpdated(updatedInventory)
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Update failed with response:', response)
        setErrors({ general: response.message || 'Failed to update inventory' })
      }
    } catch (error) {
      console.error('❌ Error updating inventory:', error)
      
      let errorMessage = 'Failed to update inventory. Please try again.'
      
      if (error.message.includes('Not found')) {
        errorMessage = 'Inventory not found. It may have been deleted by another user.'
      } else if (error.message.includes('validation')) {
        errorMessage = 'Invalid data provided. Please check your inputs.'
      }
      
      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setFormData({
        currentStock: ''
      })
      setErrors({})
      onClose()
    }
  }

  if (!isOpen || !inventory) return null

  // Get current stock status for display
  const currentStock = Number(inventory.currentStock) || 0
  const threshold = Number(inventory.product?.threshold) || 10
  const status = ProductInventoryService.getStockStatus(currentStock, threshold)
  const statusColor = ProductInventoryModel.getStatusColor(status)
  const statusLabel = ProductInventoryModel.getStatusLabel(status)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true"
          onClick={handleClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  {/* <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Edit className="h-6 w-6 text-blue-600" />
                  </div> */}
                  <div className="ml-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Edit Inventory
                    </h3>
                    
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Product Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Package className="h-8 w-8 text-gray-400 mr-3" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {inventory.product?.name || 'Unknown Product'}
                      </h4>
                      <p className="text-xs text-gray-500">
                        SKU: {inventory.product?.sku || 'N/A'} • {inventory.branch?.name || 'Unknown Branch'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                      {statusLabel}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Threshold: {threshold} {inventory.product?.unit?.abbreviation || 'units'}
                    </p>
                  </div>
                </div>
              </div>

              {/* General Error */}
              {errors.general && (
                <div className="mb-4 rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{errors.general}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Current Stock */}
                <div>
                  <label htmlFor="currentStock" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Stock *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="currentStock"
                      name="currentStock"
                      value={formData.currentStock}
                      onChange={handleInputChange}
                      disabled={loading}
                      min="0"
                      max="999999"
                      step="1"
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed ${
                        errors.currentStock ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter current stock quantity"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">
                        {inventory.product?.unit?.abbreviation || 'units'}
                      </span>
                    </div>
                  </div>
                  {errors.currentStock && (
                    <p className="mt-1 text-sm text-red-600">{errors.currentStock}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Original stock: {inventory.currentStock || 0} {inventory.product?.unit?.abbreviation || 'units'}
                  </p>
                </div>

                {/* Stock Level Indicator */}
                {formData.currentStock && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-blue-800">
                        New stock level: <strong>{Number(formData.currentStock).toLocaleString()}</strong> {inventory.product?.unit?.abbreviation || 'units'}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        ProductInventoryModel.getStatusColor(
                          ProductInventoryService.getStockStatus(Number(formData.currentStock), threshold)
                        )
                      }`}>
                        {ProductInventoryModel.getStatusLabel(
                          ProductInventoryService.getStockStatus(Number(formData.currentStock), threshold)
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-500 text-base font-medium text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  'Update Inventory'
                )}
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditProductInventoryModal